import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { FreelancerProfile, ProfileProject } from '../types/profile';
import { ActiveAlliance } from '../types/alliance';

interface AllianceParams {
  freelancerId: string;
  limitCount?: number;
  offsetCount?: number;
}

type FreelancerAlliances = {
  alliances: ActiveAlliance[];
  hasMore: boolean;
  lastSearchParams: AllianceParams | null;
  loading: boolean;
  error: string | null;
};

interface ProjectParams {
  freelancerId: string;
  limitCount?: number;
  offsetCount?: number;
}

type FreelancerProjects = {
  projects: ProfileProject[];
  hasMore: boolean;
  lastSearchParams: ProjectParams | null;
  loading: boolean;
  error: string | null;
};

interface FreelancerProfileStore {
  loading: boolean;
  error: string | null;
  freelancerProfiles: Record<string, FreelancerProfile>;

  freelancerAlliances: Record<string, FreelancerAlliances>;

  freelancerProjects: Record<string, FreelancerProjects>;

  fetch_freelancer_profile: (freelancer_id: string) => Promise<FreelancerProfile | null>;

  fetchActiveAlliances: (params: AllianceParams) => Promise<void>;
  fetchMoreActiveAlliances: (freelancerId: string) => Promise<void>;

  fetchProfileProjects: (params: ProjectParams) => Promise<void>;
  fetchMoreProjects: (freelancerId: string) => Promise<void>;
}

export const useFreelancerProfileStore = create<FreelancerProfileStore>((set, get) => {
  // Generic function to handle fetching paginated data for a freelancer
  async function fetchAndSetFreelancerData<T>(
    params: { freelancerId: string; limitCount?: number; offsetCount?: number },
    options: {
      dataKey: 'freelancerAlliances' | 'freelancerProjects';
      rpcFunction: string;
      rpcParams: { freelancer_id: string };
      append?: boolean;
    }
  ) {
    const { freelancerId, limitCount = 10, offsetCount = 0 } = params;
    const { dataKey, rpcFunction, rpcParams, append = false } = options;

    const current = get()[dataKey][freelancerId];

    if (current?.loading) return;

    const getDataArray = (item: FreelancerAlliances | FreelancerProjects | undefined): T[] => {
      if (!item) return [];

      if (dataKey === 'freelancerAlliances') return (item as FreelancerAlliances).alliances as T[];
      if (dataKey === 'freelancerProjects') return (item as FreelancerProjects).projects as T[];
      return [];
    };

    const currentData = getDataArray(current);
    if (current && currentData.length >= limitCount + offsetCount && !append) return;

    // Set loading state
    set(s => ({
      [dataKey]: {
        ...s[dataKey],
        [freelancerId]: {
          ...(dataKey === 'freelancerAlliances'
            ? { alliances: currentData as ActiveAlliance[] }
            : { projects: currentData as ProfileProject[] }),
          hasMore: current?.hasMore ?? true,
          lastSearchParams: current?.lastSearchParams ?? null,
          loading: true,
          error: null,
        },
      },
    }));

    const fetchLimit = limitCount + 1;

    try {
      const { data, error } = await supabase.rpc(rpcFunction, {
        ...rpcParams,
        limit_count: fetchLimit,
        offset_count: offsetCount,
      });

      if (error) {
        set(s => ({
          [dataKey]: {
            ...s[dataKey],
            [freelancerId]: {
              ...(dataKey === 'freelancerAlliances'
                ? { alliances: getDataArray(s[dataKey][freelancerId]) as ActiveAlliance[] }
                : { projects: getDataArray(s[dataKey][freelancerId]) as ProfileProject[] }),
              hasMore: s[dataKey][freelancerId]?.hasMore ?? false,
              lastSearchParams: s[dataKey][freelancerId]?.lastSearchParams ?? null,
              loading: false,
              error: error.message,
            },
          },
        }));
        return;
      }

      const items = (data ?? []) as T[];
      const page = items.slice(0, limitCount);
      const hasMore = items.length > limitCount;

      set(s => ({
        [dataKey]: {
          ...s[dataKey],
          [freelancerId]: {
            ...(dataKey === 'freelancerAlliances'
              ? {
                  alliances: append
                    ? [
                        ...(getDataArray(s[dataKey][freelancerId]) as ActiveAlliance[]),
                        ...(page as ActiveAlliance[]),
                      ]
                    : (page as ActiveAlliance[]),
                }
              : {
                  projects: append
                    ? [
                        ...(getDataArray(s[dataKey][freelancerId]) as ProfileProject[]),
                        ...(page as ProfileProject[]),
                      ]
                    : (page as ProfileProject[]),
                }),
            hasMore,
            lastSearchParams: { freelancerId, limitCount, offsetCount },
            loading: false,
            error: null,
          },
        },
      }));
    } catch (e) {
      set(s => ({
        [dataKey]: {
          ...s[dataKey],
          [freelancerId]: {
            ...(dataKey === 'freelancerAlliances'
              ? { alliances: getDataArray(s[dataKey][freelancerId]) as ActiveAlliance[] }
              : { projects: getDataArray(s[dataKey][freelancerId]) as ProfileProject[] }),
            hasMore: s[dataKey][freelancerId]?.hasMore ?? false,
            lastSearchParams: s[dataKey][freelancerId]?.lastSearchParams ?? null,
            loading: false,
            error: (e as Error).message,
          },
        },
      }));
    }
  }

  async function fetchAndSetAlliances(params: AllianceParams, append = false) {
    await fetchAndSetFreelancerData<ActiveAlliance>(params, {
      dataKey: 'freelancerAlliances',
      rpcFunction: 'get_freelancer_active_alliances',
      rpcParams: { freelancer_id: params.freelancerId },
      append,
    });
  }

  async function fetchAndSetProjects(params: ProjectParams, append = false) {
    await fetchAndSetFreelancerData<ProfileProject>(params, {
      dataKey: 'freelancerProjects',
      rpcFunction: 'get_freelancer_completed_projects',
      rpcParams: { freelancer_id: params.freelancerId },
      append,
    });
  }

  return {
    loading: false,
    error: null,
    freelancerProfiles: {},

    freelancerAlliances: {},
    freelancerProjects: {},

    async fetch_freelancer_profile(freelancer_id: string) {
      if (get().loading) return null;

      const cached = get().freelancerProfiles[freelancer_id];
      if (cached) return cached;

      set({ loading: true, error: null });

      const { data: profile, error } = await supabase.rpc('get_freelancer_profile', {
        freelancer_id,
      });

      if (error) {
        console.error('Error fetching freelancer profile:', error);
        set({ error: error.message, loading: false });
        return null;
      }

      if (profile) {
        set(s => ({
          loading: false,
          freelancerProfiles: {
            ...s.freelancerProfiles,
            [freelancer_id]: profile as FreelancerProfile,
          },
        }));
        return profile as FreelancerProfile;
      }

      set({ loading: false });
      return null;
    },

    fetchActiveAlliances: async (params: AllianceParams) => {
      await fetchAndSetAlliances(params, false);
    },

    fetchMoreActiveAlliances: async (freelancerId: string) => {
      const current = get().freelancerAlliances[freelancerId];
      const offsetCount = current ? current.alliances.length : 0;
      await fetchAndSetAlliances({ freelancerId, offsetCount }, true);
    },

    fetchProfileProjects: async (params: ProjectParams) => {
      await fetchAndSetProjects(params, false);
    },

    fetchMoreProjects: async (freelancerId: string) => {
      const current = get().freelancerProjects[freelancerId];
      const offsetCount = current ? current.projects.length : 0;
      await fetchAndSetProjects({ freelancerId, offsetCount }, true);
    },
  };
});
